/**
 * The one place that knows what a `WorldObject.modelUrl` under `/models/`
 * MEANS, beyond "a GLB to load" — its category (for the editor's asset
 * picker) and, for weapons specifically, whether it needs a rest-pose
 * correction when spawned loose in the world. Shared by `ultra_map_editor`
 * (grouping the picker) and `model-attach.ts` (posing weapons on load), so
 * both agree on the same taxonomy without a second hand-copied one drifting
 * from this.
 *
 * Category is derived from the asset's folder (`/models/<category>/…`)
 * rather than stored on `WorldObject` itself — it travels for free with
 * `modelUrl`, and doesn't need a new field threaded through `types.ts`,
 * `serializer.ts`, and every render site the way an authored-per-instance
 * field would (see serializer.ts's field-whitelist doc comment).
 */
export type ModelCategory = 'buildings' | 'objects' | 'items' | 'weapons' | 'vehicles';

const MODEL_CATEGORIES: readonly ModelCategory[] = ['buildings', 'objects', 'items', 'weapons', 'vehicles'];

export function categoryFromModelUrl(url: string): ModelCategory | null {
  const match = /^\/models\/([^/]+)\//.exec(url);
  const folder = match?.[1];
  return folder && (MODEL_CATEGORIES as readonly string[]).includes(folder) ? (folder as ModelCategory) : null;
}

export function isWeaponModel(url: string): boolean {
  return categoryFromModelUrl(url) === 'weapons';
}

/**
 * `vehicles/` (`veh_*.glb`) is a distinct category from `objects/` (`obj_*.glb`)
 * even though both render as static/no-clip world decoration today: a `veh_`
 * model is earmarked as the FUTURE player-drivable vehicle set — spawned next
 * to the player's home, entered/exited like an interactable, eventually with
 * its own movement/physics layer. None of that exists yet (no vehicle sim, no
 * mount/dismount, no `WorldObjectKind` for it) — this function and its
 * category only exist so the editor's asset picker can group them separately
 * from ordinary scenery and a future vehicle-mount system has a single place
 * to ask "is this modelUrl vehicle-eligible" without re-deriving the
 * `veh_` naming convention itself.
 */
export function isVehicleModel(url: string): boolean {
  return categoryFromModelUrl(url) === 'vehicles';
}

/**
 * A handful of the weapon GLBs were authored standing upright (axe/knife
 * balanced on a point — fine for a display render, wrong for a prop resting
 * on the ground) rather than lying flat like the rest of the set. Rather than
 * force every weapon through a blanket "lay it down" transform — which would
 * just as surely knock the ones that already lie flat into a wrong pose —
 * each entry here is a rotation (radians, applied in this order: X then Z)
 * measured by eye against the actual asset. Anything not listed renders with
 * no correction: "as is" is the correct default for a weapon authored lying
 * flat, and stays the in-game pose (held/displayed) once that exists.
 */
export const WEAPON_WORLD_POSE_FIXUPS: Partial<Record<string, { x?: number; z?: number }>> = {
  '/models/weapons/weapon_rusty-axe.glb': { x: Math.PI / 2 },
  '/models/weapons/weapon_rusty-knife.glb': { x: Math.PI / 2 },
};

/**
 * The player's home/shack — one GLB per upgrade level (1-6, index 0 =
 * level 1, 5 = max). `WorldObject.modelUrl` is still the only thing the
 * render pipeline actually reads (see scene.ts/model-attach.ts) — this
 * table exists so `ultra_map_editor` can offer a "Level" dropdown instead
 * of an author having to remember/paste a raw file path, and so a change
 * of level always resolves through the same canonical mapping. NOTE: there
 * is no player-progression system driving this yet (no backend "current
 * home level" exists) — a placed `WorldObject.homeLevel` is purely an
 * author's choice today, read only by the editor. Swapping which level's
 * model is live in-game is a future step, not this one.
 */
export const HOME_LEVEL_MODELS: readonly string[] = [
  '/models/buildings/building_home_lvl1.glb',
  '/models/buildings/building_home_lvl2.glb',
  '/models/buildings/building_home_lvl3.glb',
  '/models/buildings/building_home_lvl4.glb',
  '/models/buildings/building_home_lvl5.glb',
  '/models/buildings/building_home_lvl6.glb',
];

/** Clamps to [1, HOME_LEVEL_MODELS.length] — the matching url. */
export function homeLevelModelUrl(level: number): string {
  const clamped = Math.max(1, Math.min(HOME_LEVEL_MODELS.length, Math.round(level)));
  return HOME_LEVEL_MODELS[clamped - 1];
}

/** Inverse of `homeLevelModelUrl` — null for any url outside the set (including a non-home building). */
export function homeLevelFromModelUrl(url: string): number | null {
  const idx = HOME_LEVEL_MODELS.indexOf(url);
  return idx === -1 ? null : idx + 1;
}

/**
 * A handful of building GLBs were exported with their long axis on a
 * different local axis than the rest of their own level/variant family —
 * measured from each file's own bounding box (see the investigation this
 * came from), not eyeballed. Swapping `modelUrl` between siblings that
 * disagree on this would visually spin the building even though
 * `WorldObject.rotationY` never changed — this corrects the loaded model
 * itself before it's attached, composing with the parent root's own
 * `rotationY` exactly like `WEAPON_WORLD_POSE_FIXUPS` does (see
 * `model-attach.ts`'s `applyBuildingRotationFixup`).
 *
 * `building_home_lvl6.glb` USED to have a -90° entry here, inferred from its
 * bounding box being wide along local Z (0.78 x 0.41 x 1.0) where levels 1-5
 * are wide along local X. That inference was wrong, and the entry is removed.
 * Rendered in Blender, level 6's house faces its door and terrace toward
 * local -Z exactly like levels 1-5 do; what made its bbox look rotated was
 * the detached GARAGE lean-to hanging off the front, which stretches the Z
 * extent to 1.0 on its own. The house alone measures 0.77 x 0.54 — wide along
 * X, same as its siblings. Rotating it 90° would have spun a correctly-
 * oriented building. (The same garage was also pulling the model's origin off
 * the house; the GLB has since been re-centred on the house footprint so all
 * six levels share an anchor and an upgrade reads as the home growing in
 * place. See assets/ASSETS.md.)
 *
 * Kept as an empty table rather than deleted: the bbox-disagreement problem is
 * real for other building families, and `applyBuildingRotationFixup` is wired
 * up and tested.
 */
export const BUILDING_ROTATION_FIXUP: Partial<Record<string, number>> = {};

/**
 * All buildings otherwise share one default spawn height (`DEFAULT_BUILDING_HEIGHT`
 * in `ultra_map_editor/main.ts`) — these are per-model corrections against that
 * baseline, eyeballed against the actual assets: the fence/gate set reads too
 * tall at the default, guard towers too short, the watertower far too short
 * (a 1x-scale watertower reads like a garden shed). Multiplies the height
 * passed to `loadCharacterModel`, whose scale is uniform
 * (`root.scale.setScalar(...)`), so footprint width/depth shrink or grow by
 * the same factor.
 */
export const BUILDING_HEIGHT_SCALE: Partial<Record<string, number>> = {
  '/models/buildings/building_fence_1.glb': 0.8 * 1.3,
  '/models/buildings/building_fence_2.glb': 0.8 * 1.3,
  '/models/buildings/building_fence_3-long.glb': 0.8 * 1.2 * 1.3,
  '/models/buildings/building_gate_1_closed.glb': 0.8 * 1.3,
  '/models/buildings/building_guard_tower_1.glb': 1.1 * 1.3,
  '/models/buildings/building_guard_tower_2.glb': 1.1 * 1.3,
  '/models/buildings/building_watertower.glb': 2.5,
  // 2026-09-10 well-market batch (single clean Tripo meshes, unlike the same
  // day's `shacks.blend` batch — see CLUSTER_ITEMS_TODO.md). Church and
  // overseer house were re-scaled after the user judged them too small in
  // engine, against `building_settlement_market.glb` — the sea container,
  // which renders 7.59m wide at its implicit 1x. Both had come out NARROWER
  // than that container (6.26m and 4.10m), which is what made them read as
  // models rather than buildings. Now: church 9.91m wide (a landmark that
  // beats the container), overseer house 7.02m (a two-storey house that
  // matches it). The stall is unchanged — a market stall SHOULD be smaller.
  '/models/buildings/building_church.glb': 9.5 / 4,
  '/models/buildings/building_settlement-overseer_house.glb': 7.2 / 4,
  '/models/buildings/building_settlement-weapons_stall.glb': 3.0 / 4,

  // 2026-09-10 `shacks.blend` recovery batch — extracted one-object-per-file
  // out of the multi-object scene dumps with `assets/blender/scripts/
  // extract_glb_mesh.py` (see its docstring). Heights reasoned from each
  // extracted mesh's own bounding box against the 4m baseline, then rendered
  // for shape confirmation before placing.
  //
  // `building_farm` and `building_shooting_range` are CLUSTERS — a farmstead
  // with a windmill, fences and an open yard; a range with a firing lane and
  // target frames. Their solid building is a fraction of the footprint and is
  // off-centre within it, which a centred `collisionW`/`collisionD` box
  // cannot express — so both are placed `noClip` with a `CollisionZone` over
  // the building part alone. That is the "clip zone only on buildings" case.
  '/models/buildings/building_farm.glb': 4.2 / 4,
  '/models/buildings/building_shooting_range.glb': 3.2 / 4,
  '/models/buildings/building_smith.glb': 4.0 / 4,
  '/models/buildings/building_smith_2.glb': 3.8 / 4,
  '/models/buildings/building_repair_shop.glb': 3.8 / 4,
  '/models/buildings/building_workshop.glb': 3.4 / 4,
  '/models/buildings/building_house_dilapidated.glb': 4.0 / 4,
  '/models/buildings/building_shack_a.glb': 3.2 / 4,
  '/models/buildings/building_shack_b.glb': 3.4 / 4,
  '/models/buildings/building_shack_c.glb': 3.6 / 4,
  '/models/buildings/building_shack_weathered.glb': 2.8 / 4,
  '/models/buildings/building_shack_wooden.glb': 3.4 / 4,
};
// `building_settlement_market.glb` deliberately has NO entry (implicit 1x —
// the plain 4m default): its measured proportions (wide/flat, roughly a
// single-story market-stall shape) don't obviously conflict with that
// baseline the way the fence/tower/watertower set did. Unlike every entry
// above, this wasn't eyeballed live in-engine, only reasoned from the GLB's
// own bounding box — revisit if it reads too tall/short once placed.

/**
 * Same idea as `BUILDING_HEIGHT_SCALE`, one baseline down: non-building props
 * spawned through the editor's "+ Object" picker all normalize to
 * `DEFAULT_OBJECT_HEIGHT` in `ultra_map_editor/main.ts`. A barrel authored at
 * that height reads oversized against the hand-placed barrels already in
 * home_settlement.ts, so it gets scaled down against the same baseline.
 */
/**
 * ─── Changing a value here is only HALF of a resize ────────────────────────
 * This table is read by the map EDITOR, at spawn time only
 * (`ultra_map_editor/main.ts`) — it decides the `size.h` a NEWLY placed object
 * is born with. An object already baked into a map file keeps whatever
 * `size.h` it was written with, and `model-attach.ts` renders it at
 * `obj.modelHeight ?? obj.size.h`. So an entry edited here changes nothing
 * about what is currently on screen.
 *
 * Resizing a model for real means editing BOTH: this table, so future
 * placements are right, and the `size`/`collisionW`/`collisionD` of every
 * existing placement in `data/maps/*.ts`, so the ones already standing there
 * change too. The `// user: Nx` entries below were done that way.
 */
export const PROP_HEIGHT_SCALE: Partial<Record<string, number>> = {
  '/models/objects/obj_barrel.glb': 0.5 * 1.1,
  // 0.7x a 1.8m character (loadCharacterModel's default target height) against the 1.5m prop baseline.
  '/models/objects/obj_well.glb': 1.26 / 1.5,
  // Vehicles, not generic small props — DEFAULT_OBJECT_HEIGHT (1.5m) was
  // calibrated for barrels/tools, nowhere near a roof height. Reasoned from
  // a real-world target height against that 1.5m baseline (same technique
  // as the well above), NOT eyeballed live in-engine like the building set
  // — unverified until seen rendered.
  // ~1.75m roof height (a compact/mid-size SUV's roofline, roughly level
  // with a standing 1.8m character — see NPC_BODY_HEIGHT in
  // ultra_map_editor/main.ts) against the 1.5m prop baseline.
  // `obj_suv-destroyed.glb` is the trusted anchor (2026-09-10 measurement
  // batch confirmed its raw proportions render correctly) — its final
  // length (raw_h/raw_length scaled to 1.75m ≈ 4.52m) is the target every
  // other SUV-class entry below is now solved to match, because
  // loadCharacterModel scales by height alone: two Tripo exports with the
  // same "1.75m roofline" but different raw aspect ratios come out
  // different real-world LENGTHS unless the height target compensates.
  // clean-olive/dusty share one raw box (0.44 x 1.0 x 0.422), hence one value.
  '/models/objects/obj_suv-destroyed.glb': 1.75 / 1.5,
  '/models/objects/obj_suv-clean-olive.glb': 1.91 / 1.5,
  '/models/objects/obj_suv-dusty.glb': 1.91 / 1.5,
  // Started as a ~2.3m fuselage-roof guess (light-helicopter cabin,
  // excluding rotor mast) against the 1.5m prop baseline, doubled once on
  // live in-scene feedback (still too small), doubled again on further
  // feedback, then halved back down here — all three are direct visual
  // corrections, not second-guessing the base estimate underneath them.
  // NOTE: this only sets the height a NEWLY spawned heli normalizes to
  // (see `DEFAULT_OBJECT_HEIGHT` in ultra_map_editor/main.ts) — an
  // already-placed one keeps whatever `size.h` got baked in at spawn time,
  // so an existing instance needs deleting and re-spawning to pick up any
  // change here.
  '/models/objects/obj_heli-rusted.glb': 2 * (2.3 / 1.5),

  // ---- obj_car-rust-*/obj_bike_*/obj_tractor_*/obj_bulldozer/obj_trailer-tanker
  // (2026-09-09 vehicle-clutter batch) ----
  // Same technique as the SUV/heli entries above: a real-world roofline
  // height reasoned against the 1.5m `DEFAULT_OBJECT_HEIGHT` baseline, NOT
  // eyeballed live in-engine — unverified until seen rendered. Plain sedans
  // (roughly a 1.4-1.5m roofline) are left with NO entry — the 1.5m default
  // is already close enough not to need correcting, same reasoning as
  // `building_settlement_market.glb`'s deliberate omission above. Only
  // silhouettes that would visibly mismatch a sedan's height (SUVs, vans,
  // trucks, buses, motorcycles, heavy equipment) get an entry.
  // Per-vehicle height solved so each converges on obj_suv-destroyed's
  // ~4.52m final length (see the anchor note above) instead of sharing one
  // multiplier — measured 2026-09-10 (Blender bounding-box batch over all
  // 73 obj_car-rust-*/obj_suv-* GLBs, vehicle_measurements.tsv). Every
  // Tripo SUV export normalizes its longest raw axis to ~1.0 regardless of
  // actual body length, so a shared height multiplier only reproduces a
  // shared LENGTH when every file also shares the same raw
  // height-to-length ratio — they don't (0.33-0.67 across this set).
  '/models/objects/obj_car-rust-16-suv.glb': 1.73 / 1.5,
  '/models/objects/obj_car-rust-27-suv.glb': 1.86 / 1.5,
  '/models/objects/obj_car-rust-28-suv.glb': 1.51 / 1.5,
  '/models/objects/obj_car-rust-50-suv.glb': 1.78 / 1.5,
  '/models/objects/obj_car-rust-53-suv.glb': 2.04 / 1.5,
  '/models/objects/obj_car-rust-65-suv.glb': 1.74 / 1.5,
  '/models/objects/obj_car-rust-68-suv.glb': 1.78 / 1.5,
  // Left at the old shared SUV value, NOT solved like the others above:
  // this file's raw box (0.50 x 1.0 x 0.67) is far taller-for-its-length
  // than every other SUV export (0.67 vs. 0.33-0.45 elsewhere) — reads as
  // a debris-inflated/corrupted bounding box rather than a true SUV
  // proportion, so solving it against the anchor would shrink it to an
  // implausible ~3.0m height instead of fixing a real scale bug.
  '/models/objects/obj_car-rust-13-suv.glb': (1.75 * 0.85) / 1.5, // user: 0.85x
  // Panel/box vans — taller roofline than a car, shorter than a box truck.
  // Same per-vehicle fix as the SUVs above, but there's no single trusted
  // "destroyed van" anchor — solved instead so all four converge on their
  // own current average final length (~4.45m) rather than each other's
  // mismatched 4.0-4.85m spread, keeping the class's overall footprint
  // unchanged while making the four consistent with each other.
  '/models/objects/obj_car-rust-26-van.glb': (1.93 * 1.1) / 1.5, // user: 1.1x
  '/models/objects/obj_car-rust-30-van.glb': 2.1 / 1.5,
  '/models/objects/obj_car-rust-33-van.glb': 2.09 / 1.5,
  '/models/objects/obj_car-rust-44-van.glb': 2.32 / 1.5,
  // Pickup cab roofline (excludes the open bed, which sits lower).
  '/models/objects/obj_car-rust-62-pickup.glb': 1.85 / 1.5,
  '/models/objects/obj_car-rust-64-pickup.glb': 1.85 / 1.5,
  '/models/objects/obj_car-rust-52-bed.glb': 1.85 / 1.5,
  // Buses — full single-decker coach roofline.
  '/models/objects/obj_car-rust-40-bus.glb': 3.0 / 1.5,
  '/models/objects/obj_car-rust-43-bus.glb': 3.0 / 1.5,
  '/models/objects/obj_car-rust-49-bus.glb': 3.0 / 1.5,
  '/models/objects/obj_car-rust-51-bus.glb': 3.0 / 1.5,
  // Heavy trucks — cab roofline (the tallest point on each of these).
  '/models/objects/obj_car-rust-39-army.glb': (2.9 * 0.92) / 1.5, // user: 0.92x
  '/models/objects/obj_car-rust-45-army.glb': 2.9 / 1.5,
  '/models/objects/obj_car-rust-47-army.glb': 2.9 / 1.5,
  '/models/objects/obj_car-rust-46-lorry.glb': 2.9 / 1.5,
  '/models/objects/obj_car-rust-42-flatbed.glb': 2.3 / 1.5,
  '/models/objects/obj_car-rust-54-mixer.glb': 3.3 / 1.5,
  '/models/objects/obj_car-rust-57-ambulance.glb': 2.6 / 1.5,
  '/models/objects/obj_car-rust-67-firefighter.glb': (3.2 * 1.15) / 1.5, // user: 1.15x
  '/models/objects/obj_car-rust-69-crane.glb': 3.6 / 1.5,
  '/models/objects/obj_car-rust-60-RV.glb': (3.2 * 0.88) / 1.5, // user: 0.88x smaller than the original 3.2m reasoning
  '/models/objects/obj_car-rust-61-semi.glb': (3.8 * 0.95) / 1.5, // user: 0.95x
  // Low-profile bodies, shorter than a sedan.
  '/models/objects/obj_car-rust-66-limo.glb': 1.4 / 1.5,
  '/models/objects/obj_car-rust-29-cabrio.glb': 1.3 / 1.5,
  // Salvaged hood/engine-bay only, not a full car silhouette.
  '/models/objects/obj_car-rust-2-hood.glb': (0.6 * 0.7) / 1.5, // user: 0.7x
  // Motorcycles — seat/handlebar height, not roofline (no roof).
  '/models/objects/obj_bike_bobber.glb': 1.15 / 1.5,
  '/models/objects/obj_bike_classic.glb': 1.15 / 1.5,
  '/models/objects/obj_bike_street.glb': 1.15 / 1.5,
  '/models/objects/obj_bike_street_modern.glb': 1.15 / 1.5,
  '/models/objects/obj_bike_tourer.glb': 1.3 / 1.5,
  // Heavy equipment / farm machinery — cab or blade-top height.
  '/models/objects/obj_bulldozer.glb': 3.0 / 1.5,
  '/models/objects/obj_tractor_modern.glb': 2.7 / 1.5,
  '/models/objects/obj_tractor_old.glb': 2.7 / 1.5,
  // Cylindrical tank height, not a cab roofline.
  '/models/objects/obj_trailer-tanker.glb': 3.2 / 1.5,

  // ---- veh_* (2026-09-09) — see `isVehicleModel`'s doc comment: these are
  // earmarked for a future player-drivable vehicle system, currently rendered
  // as static world decoration like everything else in this table. Same
  // real-world-height-vs-1.5m-baseline reasoning as the obj_ batch above.
  '/models/vehicles/veh_bike-bobber.glb': 1.15 / 1.5,
  '/models/vehicles/veh_bike-enduro.glb': 1.3 / 1.5,
  '/models/vehicles/veh_bike-enduro-2.glb': 1.3 / 1.5,
  '/models/vehicles/veh_bike-tourer.glb': 1.3 / 1.5,
  '/models/vehicles/veh_pickup.glb': 1.85 / 1.5,
  '/models/vehicles/veh_small-pickup.glb': 1.7 / 1.5,
  '/models/vehicles/veh_van-police.glb': 2.1 / 1.5,

  // ---- 2026-09-09 items/weapons/props batch (user-supplied, 43 GLBs) ----
  // Same technique as every batch above: a real-world height reasoned
  // against the 1.5m `DEFAULT_OBJECT_HEIGHT` baseline, NOT eyeballed live
  // in-engine — unverified until seen rendered. `obj_dumpster.glb`/
  // `obj_dumpster-2.glb` and `building_cafe.glb` (in BUILDING_HEIGHT_SCALE)
  // deliberately have no entry: their reasoned real heights (~1.4m dumpster,
  // ~4m single-story cafe) don't obviously conflict with their respective
  // baselines, same reasoning as the plain-sedan/market-building omissions.
  '/models/objects/obj_carseat.glb': (0.95 * 1.15) / 1.5, // user: 1.15x larger than the original 0.95m reasoning
  '/models/objects/obj_carseat-2.glb': (0.95 * 1.15) / 1.5, // user: 1.15x larger than the original 0.95m reasoning
  '/models/objects/obj_parkbench.glb': 0.85 / 1.5,
  '/models/objects/obj_jersey-wall.glb': 0.81 / 1.5,
  '/models/objects/obj_jersey-barrier.glb': 0.81 / 1.5,
  '/models/objects/obj_street-lamp-2.glb': 4.2 / 1.5,
  '/models/objects/obj_street-lamp-3.glb': 4.2 / 1.5,
  '/models/objects/obj_street-light-1.glb': 4.2 / 1.5,
  '/models/objects/obj_jet-engine.glb': 1.8 / 1.5,
  '/models/objects/obj_sign.glb': 2.4 / 1.5,
  '/models/objects/obj_sign-2.glb': 2.6 / 1.5,
  '/models/objects/obj_tugboat-ground.glb': 3.5 / 1.5,
  '/models/objects/obj_crates.glb': 1.1 / 1.5,
  '/models/objects/obj_crates-2.glb': 1.1 / 1.5,
  '/models/objects/obj_sandbag-wall.glb': 0.6 / 1.5,
  '/models/objects/obj_tree-broken-1.glb': 2.4 / 1.5,
  '/models/objects/obj_tree-broken-2.glb': 1.8 / 1.5,
  '/models/objects/obj_rail-1.glb': 0.25 / 1.5,
  '/models/objects/obj_rail-2.glb': 0.3 / 1.5,
  '/models/objects/obj_wheelbarrow-2.glb': 0.6 / 1.5,
  '/models/objects/obj_tyre-pile.glb': 1.0 / 1.5,
  '/models/objects/obj_plane-beechcraft-crash.glb': 1.6 / 1.5,
  '/models/objects/obj_traffic-light.glb': 4.5 / 1.5,
  '/models/objects/obj_fire-barrel.glb': 0.9 / 1.5,
  // 2026-09-10, same well-market batch as the church/overseer/stall trio
  // above — single clean Tripo mesh (raw box 0.9995 x 0.8374 x 0.860,
  // longest axis on width not height). Raised from a first-pass 1.4m after
  // the user called it much too small: at 2.6m it renders 3.10m wide, a
  // walk-around industrial genset rather than something you'd carry, and
  // reads clearly as the LARGE half of the pair with `obj_generator-diesel`
  // (1.12m wide) as the small one.
  '/models/objects/obj_generator-large.glb': 2.6 / 1.5,
  // 2026-09-10 `shacks.blend` recovery batch — see BUILDING_HEIGHT_SCALE's
  // note above for the extraction. Same reasoned-height-per-raw-box method.
  '/models/objects/obj_generator-diesel.glb': 1.0 / 1.5,
  '/models/objects/obj_campfire-pit.glb': 0.5 / 1.5,
  '/models/objects/obj_sleeping-bag.glb': 0.25 / 1.5,
  '/models/objects/obj_utility-post.glb': 4.5 / 1.5,
  '/models/objects/obj_cushion.glb': 0.35 / 1.5,
  '/models/items/item_screwdriver-set.glb': 0.3 / 1.5,
  '/models/items/item_tarp-folded.glb': 0.3 / 1.5,
};
